import { useState, useRef, useEffect } from 'react';

// reactbits-style carousel: fixed-width track that slides via translateX with a
// springy ease. Cards lift + glow on hover (chroma/bento feel). Arrows advance
// one card; dots jump. <= perView cards → centered, no arrows (still hover-glow).
export default function DoctorCarousel({ children, accent = '#0D9488', cardWidth = 300, gap = 22 }) {
  const items = (Array.isArray(children) ? children : [children]).filter(Boolean);
  const viewportRef = useRef(null);
  const [perView, setPerView] = useState(3);
  const [vw, setVw] = useState(960);
  const [index, setIndex] = useState(0);
  const [hover, setHover] = useState(null);

  useEffect(() => {
    const calc = () => {
      const w = viewportRef.current?.offsetWidth || 960;
      setVw(w);
      setPerView(Math.max(1, Math.floor((w + gap) / (cardWidth + gap))));
    };
    calc();
    window.addEventListener('resize', calc);
    return () => window.removeEventListener('resize', calc);
  }, [cardWidth, gap]);

  const cellWidth = Math.max(220, Math.floor((vw - (perView - 1) * gap) / perView));
  const step = cellWidth + gap;
  const maxIndex = Math.max(0, items.length - perView);
  const clamped = Math.min(index, maxIndex);

  // reactbits "chroma-grid": each card carries its own vivid colour ring + glow.
  // The grayscale "reveal" veil lives at the SECTION level (see ChromaSection in
  // components/site/fx) so it spans full width and blends into adjacent sections.
  const CHROMA = ['#00E5FF', '#7C3AED', '#FF2D95', '#FFB020', '#22C55E', '#3B82F6', '#F43F5E', '#14B8A6'];
  const colorOf = (i) => CHROMA[i % CHROMA.length];

  const cellCss = (
    <style>{`
      .dc-cell{position:relative;overflow:hidden;border-radius:20px}
      .dc-cell>*:not(.dc-cband):not(.dc-cbeam){width:100%;height:100%;border-radius:20px!important}
      .dc-cband{position:absolute;inset:0;border-radius:20px;pointer-events:none;z-index:3;
        box-shadow:inset 0 0 0 2px var(--chroma);opacity:.4;transition:opacity .3s;}
      .dc-cbeam{position:absolute;left:0;right:0;bottom:0;height:45%;border-radius:0 0 20px 20px;pointer-events:none;z-index:2;
        background:linear-gradient(180deg, transparent, var(--chroma));opacity:.14;transition:opacity .3s;}
      .dc-cell:hover .dc-cband{opacity:1}
      .dc-cell:hover .dc-cbeam{opacity:.32}
    `}</style>
  );

  const cellStyle = (i, base) => ({
    ...base,
    '--chroma': colorOf(i),
    borderRadius: 20,
    cursor: 'pointer',
    transform: hover === i ? 'translateY(-8px)' : 'translateY(0)',
    boxShadow: hover === i ? `0 22px 52px ${colorOf(i)}55` : '0 4px 16px rgba(0,0,0,0.06)',
    transition: 'transform 0.38s cubic-bezier(.22,.61,.36,1), box-shadow 0.38s',
  });
  const cellProps = (i) => ({
    className: 'dc-cell',
    onMouseEnter: () => setHover(i),
    onMouseLeave: () => setHover((h) => (h === i ? null : h)),
  });

  // Few enough to fit — just center them, no carousel.
  if (items.length <= perView) {
    return (
      <div style={{ display: 'flex', gap, justifyContent: 'center', flexWrap: 'wrap', alignItems: 'stretch' }}>
        {cellCss}
        {items.map((c, i) => (
          <div key={c?.key ?? i} {...cellProps(i)} style={cellStyle(i, { width: cardWidth, maxWidth: '88vw', display: 'flex' })}>{c}<span className="dc-cbeam" /><span className="dc-cband" /></div>
        ))}
      </div>
    );
  }

  const arrowStyle = (side) => ({
    position: 'absolute', top: 'calc(50% - 22px)', [side]: -6, zIndex: 3,
    width: 46, height: 46, borderRadius: '50%', cursor: 'pointer',
    background: 'rgba(255,255,255,0.95)', color: accent, border: `1px solid ${accent}33`,
    boxShadow: '0 6px 20px rgba(0,0,0,0.18)', outline: 'none',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    transition: 'transform 0.2s, background 0.2s',
  });
  const Chevron = ({ dir }) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d={dir === 'left' ? 'M15 18l-6-6 6-6' : 'M9 18l6-6-6-6'} />
    </svg>
  );

  return (
    <div style={{ position: 'relative' }}>
      {clamped > 0 && (
        <button type="button" aria-label="Previous"
          onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.08)')}
          onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
          onClick={() => setIndex((i) => Math.max(0, Math.min(i, maxIndex) - 1))} style={arrowStyle('left')}><Chevron dir="left" /></button>
      )}

      <div ref={viewportRef} style={{ overflow: 'hidden', padding: '8px 4px 18px' }}>
        <div style={{ display: 'flex', gap, alignItems: 'stretch', transform: `translateX(-${clamped * step}px)`, transition: 'transform 0.6s cubic-bezier(.22,.61,.36,1)' }}>
          {cellCss}
          {items.map((c, i) => (
            <div key={c?.key ?? i} {...cellProps(i)} style={cellStyle(i, { flex: `0 0 ${cellWidth}px`, display: 'flex' })}>{c}<span className="dc-cbeam" /><span className="dc-cband" /></div>
          ))}
        </div>
      </div>

      {clamped < maxIndex && (
        <button type="button" aria-label="Next"
          onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.08)')}
          onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
          onClick={() => setIndex((i) => Math.min(maxIndex, Math.min(i, maxIndex) + 1))} style={arrowStyle('right')}><Chevron dir="right" /></button>
      )}

      {/* dots */}
      <div style={{ display: 'flex', gap: 6, justifyContent: 'center', marginTop: 6 }}>
        {Array.from({ length: maxIndex + 1 }).map((_, i) => (
          <button key={i} type="button" aria-label={`Go to ${i + 1}`} onClick={() => setIndex(i)}
            style={{ width: i === clamped ? 24 : 8, height: 8, borderRadius: 4, border: 'none', cursor: 'pointer',
              background: i === clamped ? accent : `${accent}40`, transition: 'all 0.3s', padding: 0 }} />
        ))}
      </div>
    </div>
  );
}
